import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Lock, PartyPopper } from "lucide-react";
import PolicyAckStep from "@/components/host/PolicyAckStep";
import {
  Bullet, WelcomeVisual, DashboardVisual, ScheduleVisual,
  DASHBOARD_POINTS, SCHEDULE_POINTS, DISCORD_POINTS,
} from "@/components/host/OnboardingSteps";

const STEPS = ["Welcome", "Host Dashboard", "Schedule", "Discord", "Policies"];

function StepRail({ step, tourDone }) {
  return (
    <ol className="flex flex-wrap gap-px bg-[var(--ff-border)] w-fit mb-10" data-testid="onb-step-rail">
      {STEPS.map((s, i) => (
        <li key={s} className={`px-4 py-2 font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] ${i === step ? "bg-[var(--ff-red)] text-white" : i < step || (tourDone && i < 4) ? "bg-[var(--ff-surface)] text-white" : "bg-[var(--ff-bg)] text-[var(--ff-text-dim)]"}`}>
          {String(i + 1).padStart(2, "0")} {s}
        </li>
      ))}
    </ol>
  );
}

function TourStep({ eyebrow, title, intro, points, visual }) {
  return (
    <div className="grid lg:grid-cols-2 gap-10 items-start animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="space-y-6">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-2">{eyebrow}</div>
          <h2 className="font-display text-3xl lg:text-4xl leading-none mb-3">{title}</h2>
          <p className="text-[var(--ff-text-dim)] text-sm leading-relaxed max-w-lg">{intro}</p>
        </div>
        <div className="space-y-5">{points.map((p) => <Bullet key={p.title} icon={p.icon} title={p.title}>{p.text}</Bullet>)}</div>
      </div>
      <div>{visual}</div>
    </div>
  );
}

export default function HostOnboarding({ state, onComplete }) {
  const reReadOnly = state.tour_completed;
  const [step, setStep] = useState(reReadOnly ? 4 : 0);
  const [prog, setProg] = useState({ read: 0, total: 0 });
  const [busy, setBusy] = useState(false);

  const next = async () => {
    if (step === 3) {
      try { await api.post("/host/onboarding/tour-complete"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return; }
    }
    setStep(step + 1);
  };

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/host/onboarding");
      if (!data.complete) { toast.error("Please read every policy first."); return; }
      toast.success("Onboarding complete — welcome to Host Studio!");
      onComplete();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const allRead = prog.total === prog.read;
  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24" data-testid="host-onboarding">
      <div className="flex items-center gap-2 label-eyebrow mb-3"><Lock size={11} className="text-[var(--ff-red)]" /> HOST STUDIO LOCKED UNTIL ONBOARDING IS COMPLETE</div>
      {reReadOnly && (
        <div className="border border-yellow-300/40 bg-yellow-300/5 p-4 mb-8 text-sm" data-testid="onb-reread-banner">
          <b className="text-yellow-300">Policies were added or updated.</b> Read them below to get back into your Host Dashboard.
        </div>
      )}
      <StepRail step={step} tourDone={reReadOnly} />

      {step === 0 && <TourStep eyebrow="▮ STEP 01 — WELCOME" title={`WELCOME, ${(state.host_name || "HOST").toUpperCase()}.`}
        intro="You've been given the Host role at Freshy Fresh Productions. This quick tour walks you through your Host Dashboard and the Schedule, then you'll read our company policies. It takes about three minutes."
        points={[]} visual={<WelcomeVisual name={state.host_name} />} />}
      {step === 1 && <TourStep eyebrow="▮ STEP 02 — HOST DASHBOARD" title="YOUR HOST STUDIO."
        intro="Host Studio is your control room. Here's what lives in each tab." points={DASHBOARD_POINTS} visual={<DashboardVisual />} />}
      {step === 2 && <TourStep eyebrow="▮ STEP 03 — THE SCHEDULE" title="WHEN YOU'RE ON AIR."
        intro="Your shows and air times are managed by admins and published on the network schedule." points={SCHEDULE_POINTS} visual={<ScheduleVisual shows={state.my_shows || []} />} />}
      {step === 3 && <TourStep eyebrow="▮ STEP 04 — DISCORD" title="STAY CONNECTED."
        intro="Our Discord bot is how the network reaches you fast." points={DISCORD_POINTS} visual={<WelcomeVisual name="Freshy Fresh Bot" />} />}
      {step === 4 && (
        <div className="max-w-3xl">
          <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ STEP 05 — COMPANY POLICIES</div>
          <h2 className="font-display text-3xl lg:text-4xl leading-none mb-3">READ EVERY POLICY.</h2>
          <p className="text-[var(--ff-text-dim)] text-sm mb-8">Open each policy, read it, and confirm. Your Host Dashboard unlocks once all are confirmed.</p>
          <PolicyAckStep onProgress={(read, total) => setProg({ read, total })} />
        </div>
      )}

      <div className="mt-12 pt-6 border-t border-[var(--ff-border)] flex items-center justify-between">
        {step > 0 && !(reReadOnly && step === 4)
          ? <button onClick={() => setStep(step - 1)} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="onb-back-btn"><ArrowLeft size={12} /> Back</button>
          : <span />}
        {step < 4 ? (
          <button onClick={next} className="btn-primary" data-testid="onb-next-btn">{step === 0 ? "Start Tour" : "Next"} <ArrowRight size={14} /></button>
        ) : (
          <button onClick={finish} disabled={!allRead || busy} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid="onb-finish-btn">
            <PartyPopper size={14} /> {allRead ? "Unlock Host Studio" : `Read ${prog.total - prog.read} more`}
          </button>
        )}
      </div>
    </div>
  );
}
